import { useQuery } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useTranslation } from 'react-i18next';
import { useContext } from 'react';
import { AuthenticationContext } from '~/components/Authentication/AuthenticationProvider';
import { StatsPageInfo, StatCard } from '~/components/Shared/StatsPageInfo/StatsPageInfo';
import dayjs, { Dayjs } from 'dayjs';

interface TransactionsStatistics {
  total: number | null;
  created: number | null;
  success: number | null;
  canceled: number | null;
  waiting: number | null;
}

interface Props {
  statusFilter: string;
  setStatusFilter: (value: string) => void;
  dates: [Dayjs, Dayjs];
}

const AllIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6"/>
    <line x1="8" y1="12" x2="21" y2="12"/>
    <line x1="8" y1="18" x2="21" y2="18"/>
    <line x1="3" y1="6" x2="3.01" y2="6"/>
    <line x1="3" y1="12" x2="3.01" y2="12"/>
    <line x1="3" y1="18" x2="3.01" y2="18"/>
  </svg>
);

const PreparingIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

const ReceivedIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const WaitingIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

const CanceledIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="15" y1="9" x2="9" y2="15"/>
    <line x1="9" y1="9" x2="15" y2="15"/>
  </svg>
);

const QrIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7"/>
    <rect x="14" y="3" width="7" height="7"/>
    <rect x="14" y="14" width="7" height="7"/>
    <rect x="3" y="14" width="7" height="7"/>
  </svg>
);

export const TransactionsPageInfo = ({ statusFilter, setStatusFilter, dates }: Props) => {
  const { t } = useTranslation();
  const { user } = useContext(AuthenticationContext);

  const formattedDates = [
    dayjs(dates[0])?.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
    dayjs(dates[1])?.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
  ];

  const { data: stats } = useQuery<TransactionsStatistics>({
    queryFn: async () => {
      const response = await instance.get('admin/food-orders-statistics', {
        params: { dates: formattedDates },
      });
      return response.data;
    },
    queryKey: ['food-orders-statistics', formattedDates],
  });

  const { data: qrInfo } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get('admin/kso-order-statistic', {
        params: {
          asset_ids: [user?.data.asset_id],
          dates: formattedDates,
        },
      });
      return data;
    },
    queryKey: ['kso-order-statistic', user?.data.asset_id, formattedDates],
  });

  const cards: StatCard[] = [
    {
      value: '__qr__',
      label: t('qr_generations'),
      count: qrInfo?.total ?? null,
      color: 'teal',
      icon: <QrIcon />,
      disabled: true,
    },
    { value: '', label: t('total'), count: stats?.total, color: 'purple', icon: <AllIcon /> },
    { value: '1', label: t('in_preparation'), count: stats?.created, color: 'orange', icon: <PreparingIcon /> },
    { value: '4', label: t('waiting_for_pickup'), count: stats?.waiting, color: 'blue', icon: <WaitingIcon /> },
    { value: '2', label: t('received'), count: stats?.success, color: 'green', icon: <ReceivedIcon /> },
    { value: '3', label: t('canceled'), count: stats?.canceled, color: 'red', icon: <CanceledIcon /> },
  ];

  return (
    <StatsPageInfo
      activeStatus={statusFilter}
      onStatusChange={setStatusFilter}
      cards={cards}
    />
  );
};
